import { useCallback, useEffect, useRef, useState } from "react";
import { computePollDelay, type PollBackoff } from "@admin/lib/poll";

export type UsePollOptions<T> = {
  /** When false, cancel timers and do not fetch. Default true. */
  enabled?: boolean;
  /** Healthy-path delay between ticks (ms). */
  intervalMs: number;
  backoff?: PollBackoff;
  /** +/- fraction of base delay. Default 0.2. */
  jitterRatio?: number;
  /** If true, run one immediate retry before counting the failure. */
  retryOnceOn?: (err: unknown) => boolean;
  /** Delay before the one-shot retry. Default 500. */
  retryOnceDelayMs?: number;
  /** Stop scheduling further ticks when this returns true for successful data. */
  stopWhen?: (data: T) => boolean;
  /** Skip ticks while the document is hidden. Default true. */
  pauseWhenHidden?: boolean;
  /** Run a tick immediately when enabled. Default true. */
  immediate?: boolean;
};

export type UsePollResult<T> = {
  data: T | null;
  error: Error | null;
  isPolling: boolean;
  refresh: () => Promise<void>;
};

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Recursive setTimeout poller for admin console GETs.
 * Keep fetchers in admin/lib/api.ts; this hook only owns scheduling.
 */
export function usePoll<T>(
  fetcher: () => Promise<T>,
  options: UsePollOptions<T>,
): UsePollResult<T> {
  const {
    enabled = true,
    intervalMs,
    backoff,
    jitterRatio = 0.2,
    retryOnceOn,
    retryOnceDelayMs = 500,
    stopWhen,
    pauseWhenHidden = true,
    immediate = true,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const stopWhenRef = useRef(stopWhen);
  stopWhenRef.current = stopWhen;
  const retryOnceOnRef = useRef(retryOnceOn);
  retryOnceOnRef.current = retryOnceOn;

  const failureCountRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const stoppedRef = useRef(false);
  const aliveRef = useRef(true);
  const runTickRef = useRef<(opts?: { force?: boolean }) => Promise<void>>(async () => {});

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback(() => {
    clearTimer();
    if (!aliveRef.current || !enabled || stoppedRef.current) return;

    const delay = computePollDelay({
      intervalMs,
      failureCount: failureCountRef.current,
      backoffFactor: backoff?.factor,
      backoffMaxMs: backoff?.maxMs,
      jitterRatio,
    });

    timerRef.current = window.setTimeout(() => {
      void runTickRef.current();
    }, delay);
  }, [backoff?.factor, backoff?.maxMs, clearTimer, enabled, intervalMs, jitterRatio]);

  const runTick = useCallback(async (opts?: { force?: boolean }) => {
    if (!aliveRef.current || stoppedRef.current) return;
    if (!opts?.force && !enabled) return;
    if (inFlightRef.current) return;

    if (
      !opts?.force &&
      pauseWhenHidden &&
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      scheduleNext();
      return;
    }

    inFlightRef.current = true;
    setIsPolling(true);
    try {
      let result: T;
      try {
        result = await fetcherRef.current();
      } catch (firstErr) {
        const retry = retryOnceOnRef.current?.(firstErr) === true;
        if (!retry) throw firstErr;
        await sleep(retryOnceDelayMs);
        if (!aliveRef.current || stoppedRef.current) return;
        if (!opts?.force && !enabled) return;
        result = await fetcherRef.current();
      }

      if (!aliveRef.current) return;
      setData(result);
      setError(null);
      failureCountRef.current = 0;

      if (stopWhenRef.current?.(result)) {
        stoppedRef.current = true;
        clearTimer();
        return;
      }
      if (enabled) scheduleNext();
    } catch (err) {
      if (!aliveRef.current) return;
      setError(toError(err));
      failureCountRef.current += 1;
      if (enabled) scheduleNext();
    } finally {
      inFlightRef.current = false;
      if (aliveRef.current) setIsPolling(false);
    }
  }, [clearTimer, enabled, pauseWhenHidden, retryOnceDelayMs, scheduleNext]);

  runTickRef.current = runTick;

  const refresh = useCallback(async () => {
    stoppedRef.current = false;
    clearTimer();
    await runTick({ force: true });
  }, [clearTimer, runTick]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  useEffect(() => {
    stoppedRef.current = false;
    clearTimer();

    if (!enabled) {
      return;
    }

    if (immediate) {
      void runTick();
    } else {
      scheduleNext();
    }

    const onVisibility = () => {
      if (!pauseWhenHidden) return;
      if (document.visibilityState === "visible" && !inFlightRef.current && !stoppedRef.current) {
        void runTick();
      }
    };
    if (pauseWhenHidden && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      clearTimer();
      if (pauseWhenHidden && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [clearTimer, enabled, immediate, pauseWhenHidden, runTick, scheduleNext]);

  return { data, error, isPolling, refresh };
}
